import { IUnitLessonParams } from './types'
import {
  getAllTableValuesForKey,
  getTableValueForKey,
  stringTransform,
} from './gDriveUtils'
import {
  createTable,
  createUnorderedList,
  PARAGRAPH_SPACING,
  wrapB,
  wrapP,
} from '../html_utility'
import { createNoteNode } from '../nodeCreationHelpers'

export function createMaterialPrep(params: IUnitLessonParams) {
  return [
    createNoteNode({
      title: 'Materials Preparation And List',
      value: getNoteContent(params),
      studentFacing: false,
    }),
  ]
}

function getNoteContent(params: IUnitLessonParams) {
  return [...getMaterialsPrep(params), ...getMaterialsList(params)].join(
    PARAGRAPH_SPACING,
  )
}

function getMaterialsPrep(params: IUnitLessonParams) {
  const standardPrep = [
    [
      'Review teacher guide, slides, and teacher references or keys (if applicable).',
      'Make copies of handouts and ensure sufficient copies of student references, readings, and procedures are available.',
    ]
      .map((s) => wrapP(s))
      .join(PARAGRAPH_SPACING),
  ]
  let title = 'Materials preparation'

  const [time, ...rest] = [
    'time',
    'other-materials-preparation',
    'lab-preparation',
  ].reduce<string[]>((acc, valueKey) => {
    const value = getTableValueForKey({
      tableName: '[table:materials-preparation]',
      tableData: params.lessonDoc.tableContent,
      valueKey,
      transform: stringTransform,
    })
    return [...acc, value]
  }, [])

  if (time) {
    title += ` (${time} minutes)`
  }

  rest.forEach((item) => item && standardPrep.push(wrapP(item)))

  return [wrapP(wrapB(title)), ...standardPrep]
}

function getMaterialsList(params: IUnitLessonParams) {
  const table = getMaterialsTable(params)
  const title = 'Materials List'
  return [wrapP(wrapB(title)), table]
}

function getMaterialsTable(params: IUnitLessonParams) {
  const groups = getGroupedActivityMaterials(params)
  return createTable(
    [
      ['Per student', 'Per group', 'Per class'].map((s) => wrapB(s)),
      [
        createUnorderedList(groups.student),
        createUnorderedList(groups.group),
        createUnorderedList(groups.class),
      ],
    ],
    {
      colWidths: [33, 33, 33],
      cellStyle(rowIndex) {
        if (rowIndex === 0) {
          return { style: 'text-align: center; vertical-align: top;' }
        }
        return { style: 'vertical-align: top;' }
      },
      tableStyle: { border: '1px solid;', 'border-collapse': 'collapse' },
    },
  )
}

function getGroupedActivityMaterials(params: IUnitLessonParams) {
  const groups: { student: string[]; class: string[]; group: string[] } = {
    student: [],
    class: [],
    group: [],
  }
  const addItemsToGroup = (items: string[], groupKey: keyof typeof groups) => {
    const groupList = groups[groupKey]
    items.forEach((item) => {
      item
        .split(',')
        .map((i) => i.trim())
        .forEach((i) => {
          if (!groupList.includes(i)) {
            groupList.push(i)
          }
        })
    })
  }
  const dataItems: { valueKey: string; group: keyof typeof groups }[] = [
    { valueKey: 'lab-materials-student', group: 'student' },
    { valueKey: 'lab-materials-group', group: 'group' },
    { valueKey: 'lab-materials-class', group: 'class' },
    { valueKey: 'activity-materials-student', group: 'student' },
    { valueKey: 'activity-materials-group', group: 'group' },
    { valueKey: 'activity-materials-class', group: 'class' },
  ]
  dataItems.forEach(({ valueKey, group }) => {
    const values = getAllTableValuesForKey({
      tableName: 'activity-metadata',
      tableData: params.lessonDoc.tableContent,
      valueKey,
      transform: stringTransform,
      options: { unique: true, filtered: true },
    })
    addItemsToGroup(values, group)
  })
  return groups
}
